import { useEffect, useMemo } from 'react';

import { Box, Select, SelectContent, SelectTrigger } from '@eluve/components';
import { useIsUserAppointmentOwner } from '@eluve/frontend-appointment-hooks';
import {
  PromptTemplateSelectItem,
  usePromptTemplates,
} from '@eluve/frontend-feature-user-settings';
import { useAnalytics } from '@eluve/posthog-react';

interface OutputTemplateSelectorProps {
  selectedOutputTemplateId?: string;
  onSelectionChanged: (initialOutputTemplateId: string) => void;
  usesPortal?: boolean;
  side?: 'bottom' | 'left' | 'right' | 'top';
}

export const OutputTemplateSelector: React.FC<OutputTemplateSelectorProps> = ({
  selectedOutputTemplateId,
  onSelectionChanged,
  usesPortal,
  side,
}) => {
  const {
    activeOutputTemplates: outputTemplates,
    defaultOutputTemplateId,
    userSettingsOutputTemplateId,
  } = usePromptTemplates();

  const isUserApptOwner = useIsUserAppointmentOwner();
  const analytics = useAnalytics('output-template-selector');

  const handleOpenChange = (open: boolean) => {
    if (open) {
      analytics.capture('appointment.template_dropdown_clicked', {
        template_count: outputTemplates.length,
        template_type: 'output',
      });
    }
  };

  const handleValueChange = (outputTemplateId: string, isUserAction = true) => {
    // Only track when user explicitly selects a template
    if (isUserAction) {
      analytics.capture('appointment.template_selected', {
        template_id: outputTemplateId,
        template_type: 'output',
      });
    }
    onSelectionChanged(outputTemplateId);
  };

  useEffect(() => {
    if (!isUserApptOwner) {
      // do not set defaults if user is not appointment owner
      return;
    }

    // select the default output template if no output template is selected
    if (!selectedOutputTemplateId && userSettingsOutputTemplateId) {
      handleValueChange(userSettingsOutputTemplateId, false);
    }

    if (
      !selectedOutputTemplateId &&
      !userSettingsOutputTemplateId &&
      defaultOutputTemplateId
    ) {
      handleValueChange(defaultOutputTemplateId, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOutputTemplateId,
    defaultOutputTemplateId,
    userSettingsOutputTemplateId,
    outputTemplates,
    isUserApptOwner,
  ]);

  const selectedOutputTemplate = useMemo(
    () =>
      outputTemplates.find(
        (template) => template.id === selectedOutputTemplateId,
      ),
    [outputTemplates, selectedOutputTemplateId],
  );

  return (
    <Box vStack className="w-full">
      <Select
        value={selectedOutputTemplateId}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
        disabled={!isUserApptOwner}
      >
        <SelectTrigger>
          {selectedOutputTemplate?.name ?? 'Select Output Template'}
        </SelectTrigger>
        <SelectContent side={side} usesPortal={usesPortal}>
          {outputTemplates?.map((template) => (
            <PromptTemplateSelectItem
              key={template.id}
              templateId={template.id ?? ''}
              templateName={template.name ?? ''}
              templateDescription={null}
            />
          ))}
        </SelectContent>
      </Select>
    </Box>
  );
};
